import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Upload, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ImageUpload = ({ images, setImages, maxImages = 10, label = "Images" }) => {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    const filesToUpload = files.slice(0, remainingSlots);

    if (filesToUpload.length === 0) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    const newImages = [];
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setUploadProgress(`Uploading ${i + 1} of ${filesToUpload.length}...`);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        failCount++;
        continue;
      }

      // Allow larger files - backend will compress them automatically
      // Phone photos can be 10MB+, we accept up to 15MB
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 15MB)`);
        failCount++;
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        // Show progress for larger files
        setUploadProgress(`Uploading & optimizing ${i + 1} of ${filesToUpload.length}...`);
        
        console.log('Uploading file:', file.name, 'Size:', file.size);
        
        const res = await api().post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000 // 60 second timeout for larger phone photos
        });
        
        console.log('Upload response:', res.data);
        
        if (res.data && res.data.url) {
          newImages.push(res.data.url);
          successCount++;
        } else {
          console.error('No URL in response:', res.data);
          toast.error(`Failed to upload ${file.name}`);
          failCount++;
        }
      } catch (err) {
        console.error('Upload failed:', err);
        const errorMsg = err.response?.data?.detail || err.message || 'Upload failed';
        toast.error(`Failed to upload ${file.name}: ${errorMsg}`);
        failCount++;
      }
    }

    if (newImages.length > 0) {
      setImages([...images, ...newImages]);
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully`);
    }
    
    if (failCount > 0 && successCount === 0) {
      toast.error('All uploads failed. Please try again.');
    }

    setUploading(false);
    setUploadProgress('');
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    toast.success('Image removed');
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-stone-700">
        {label} ({images.length}/{maxImages})
      </label>
      
      {/* Image Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {/* Existing Images */}
        {images.map((img, idx) => (
          <div key={idx} className="relative aspect-square rounded-sm overflow-hidden border border-stone-200 group bg-stone-100">
            <img 
              src={img} 
              alt={`Upload ${idx + 1}`} 
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Image load error:', img.substring(0, 50));
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f5f5f4" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%2378716c" font-size="12">Error</text></svg>';
              }}
            />
            <button
              type="button"
              onClick={() => removeImage(idx)}
              className="absolute top-1 right-1 p-1.5 bg-gradient-to-r from-sky-500 to-teal-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
              data-testid={`remove-image-${idx}`}
            >
              <X className="h-3 w-3" />
            </button>
            <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
              {idx + 1}
            </div>
          </div>
        ))}
        
        {/* Upload Button */}
        {images.length < maxImages && (
          <button
            type="button"
            onClick={handleClick}
            disabled={uploading}
            className="aspect-square rounded-sm border-2 border-dashed border-stone-300 hover:border-teal-500 hover:bg-teal-50 flex flex-col items-center justify-center gap-2 text-stone-400 hover:text-teal-600 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="upload-image-button"
          >
            {uploading ? (
              <>
                <div className="animate-spin h-6 w-6 border-2 border-stone-300 border-t-teal-600 rounded-full" />
                <span className="text-xs text-center px-2">{uploadProgress || 'Uploading...'}</span>
              </>
            ) : (
              <>
                <Upload className="h-6 w-6" />
                <span className="text-xs font-medium">Add Photo</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />
      
      {/* Help Text */}
      <div className="flex items-start gap-2 text-xs text-stone-500">
        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <p>Upload up to {maxImages} images. Photos from your phone will be automatically optimized.</p>
          <p className="mt-1">Tip: Select photos directly from your gallery - we'll handle the rest!</p>
        </div>
      </div>

      {/* Debug info - can be removed in production */}
      {images.length > 0 && (
        <p className="text-xs text-green-600">
          ✓ {images.length} image{images.length > 1 ? 's' : ''} ready
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
